import React, { useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileSpreadsheet } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import { Loader } from '@ui';

const ZOOM_LEVELS = [0.5, 0.75, 0.9, 1, 1.25, 1.5, 2];
// Pad the preview beyond the data with empty rows/columns so it reads like an open spreadsheet.
export const gridColCount = (dataCols: number): number => Math.max(dataCols + 6, 18);

/** Only pad a sheet shown whole: blanks after a truncated one read as the end of the data. */
export const gridRowCount = (shownRows: number, isTruncated: boolean): number =>
  isTruncated ? shownRows + 1 : Math.max(shownRows + 1 + 12, 60);
// Spreadsheet column label: 0 → A, 25 → Z, 26 → AA, …
const colLetter = (index: number): string => {
  let n = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};

export interface SpreadsheetSheet {
  name: string; // worksheet name, as it appears on the tab in Excel
  columns: (string | number)[]; // header row (drawn as row 1)
  rows: (string | number)[][]; // full data set; only the first `previewLimit` are rendered
}

export interface SpreadsheetPreviewProps {
  filename: string;
  sheets: SpreadsheetSheet[]; // one entry per worksheet; a tab bar shows when there's more than one
  loading?: boolean;
  previewLimit?: number;
  heightClass?: string; // height of the scrollable grid area (Tailwind class)
  /** Fill the parent rather than a fixed height, which left the caller sizing around the chrome by hand. */
  fill?: boolean;
}

// A framed mini-spreadsheet that reads like the file you'll export: a window title bar (file name, zoom,
// row count) over a scrollable grid with column letters and row numbers, padded with empty rows and columns
// so it looks like an open sheet. It renders the same arrays the download is built from, so "what you see is
// what you get" holds by construction rather than by care.
//
// Shared rather than owned by Insights: the client portal's sprint export previews its workbook the same way.
const SpreadsheetPreview = ({
  filename,
  sheets,
  loading = false,
  previewLimit = 200,
  heightClass = 'h-[380px]',
  fill = false,
}: SpreadsheetPreviewProps): JSX.Element => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [zoomOpen, setZoomOpen] = useState(false);
  // Which zoom row is visually highlighted: the hovered one, else the current zoom.
  const [zoomHover, setZoomHover] = useState<number | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  // Clamp rather than reset on change, so a shrinking sheet list can't leave us pointing past the end.
  const active = Math.min(activeSheet, Math.max(sheets.length - 1, 0));
  const { columns, rows } = sheets[active] ?? { columns: [], rows: [] };
  const previewRows = rows.slice(0, previewLimit);
  const isTruncated = rows.length > previewLimit;

  return (
    <div
      className={cx(
        'overflow-hidden rounded-xl border-4 border-border bg-card shadow-lg',
        fill && 'flex h-full min-h-0 flex-col',
      )}
    >
      {/* Window title bar — frames the grid as the actual file you'll export. */}
      <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/50 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <FileSpreadsheet className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
          <span className="truncate text-sm font-medium text-foreground">{filename}</span>
        </div>
        <div className="flex items-center gap-3">
          <Popover
            open={zoomOpen}
            onOpenChange={o => {
              setZoomOpen(o);
              setZoomHover(null);
            }}
          >
            <PopoverTrigger
              type="button"
              aria-label={t('zoom')}
              className="flex h-7 items-center gap-1 rounded-md border border-transparent px-1.5 text-sm tabular-nums text-foreground outline-none transition-colors hover:bg-muted focus-visible:bg-muted"
            >
              {Math.round(zoom * 100)}%
              <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
            </PopoverTrigger>
            <PopoverContent align="end" className="w-20 p-1">
              <div onMouseLeave={() => setZoomHover(null)}>
                {ZOOM_LEVELS.map(z => (
                  <button
                    key={z}
                    type="button"
                    onMouseEnter={() => setZoomHover(z)}
                    onClick={() => {
                      setZoom(z);
                      setZoomOpen(false);
                    }}
                    className={cx(
                      'flex w-full items-center rounded-md px-2 py-1 text-sm tabular-nums text-foreground transition-colors',
                      (zoomHover ?? zoom) === z && 'bg-muted',
                      z === zoom && 'font-medium',
                    )}
                  >
                    {Math.round(z * 100)}%
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {/* Nothing about the count while it is still being read: "0 rows" beside a spinner reads as an
              empty export rather than one that has not arrived. */}
          <span className="shrink-0 text-xs text-muted-foreground">
            {loading ? t('loading') : t('nRows', { count: rows.length })}
          </span>
        </div>
      </div>
      {loading ? (
        <div className={cx('flex justify-center py-16', fill && 'min-h-0 flex-1 items-center')}>
          <Loader />
        </div>
      ) : (
        <div className={cx('overflow-auto overscroll-contain', fill ? 'min-h-0 flex-1' : heightClass)}>
          {/* Row 1 is the header (as the exported .xlsx opens). */}
          <table className="w-max border-separate border-spacing-0 text-[11px]" style={{ zoom }}>
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-20 w-10 border-b border-r border-border bg-muted">
                  <span className="sr-only">#</span>
                </th>
                {Array.from({ length: gridColCount(columns.length) }, (_, ci) => (
                  <th
                    key={colLetter(ci)}
                    className="sticky top-0 z-10 min-w-[6rem] border-b border-r border-border bg-muted px-2 py-1 text-center font-medium text-muted-foreground"
                  >
                    {colLetter(ci)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: gridRowCount(previewRows.length, isTruncated) }, (_, ri) => {
                const dataRow = ri === 0 ? columns : previewRows[ri - 1];
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={ri}>
                    <td className="sticky left-0 z-10 border-b border-r border-border bg-muted px-2 py-1 text-center text-muted-foreground">
                      {ri + 1}
                    </td>
                    {Array.from({ length: gridColCount(columns.length) }, (_, ci) => {
                      const cell = dataRow?.[ci] ?? '';
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <td
                          key={ci}
                          className={cx(
                            'h-6 max-w-[22rem] truncate border-b border-r border-border px-2 py-1',
                            ri === 0 ? 'bg-muted/40 font-semibold text-foreground' : 'text-foreground',
                          )}
                          title={cell === '' ? undefined : String(cell)}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && isTruncated && (
        <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {t('previewTruncated', { shown: previewLimit, total: rows.length })}
        </p>
      )}
      {/* Worksheet tabs, Excel-style along the bottom — only when the file has more than one sheet. */}
      {sheets.length > 1 && (
        <div className="flex items-center gap-1 border-t border-border bg-muted/50 px-2 py-1.5">
          {sheets.map((sheet, index) => (
            <button
              key={sheet.name}
              type="button"
              onClick={() => setActiveSheet(index)}
              aria-current={index === active}
              className={cx(
                'rounded-md px-2.5 py-1 text-xs transition-colors',
                index === active
                  ? 'bg-card font-semibold text-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpreadsheetPreview;
